import { apiAction, API_SUCCESS, API_ERROR } from "../dux/api";
import { apiCallComplete, apiCallStart } from "../dux/apiStatus";
import { openSnackbar } from "../dux/snackbar";
import {
  fetchBookNowDetail,
  FETCH_BOOK_NOW_DETAILS,
  MAKE_SLOT_AVAILABLE,
  NEW_BOOKING,
  setBookNowDetail,
  setNewBookingData,
  VERIFY_PAYMENT,
  setHolidayBookingStatus,
  setDatePresenceStatus,
  setDayMappings,
  setPricingConfigs,
  setAvailableSlots,
  setEquipmentConfigs,
} from "../dux/bookNow";
import { completeBooking } from "../dux/ui/uiBookNow";
import {
  getAvailableSlotsUrl,
  getCreateBookingUrl,
  getVerifyPaymentUrl,
  getPricingConfigsUrl,
  getDayMappingsUrl,
  getCheckBookingAllowedUrl,
  getCheckDatePresenceUrl,
  getEquipmentOptionsUrl,
} from "../helpers/apiEndPoints";
import {
  parseBookNowApiData,
  parseNewBookingApiData,
} from "../helpers/bookNowHelper";

const bookNow =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    next(action);

    // Store original parameters in closure for the entire middleware chain
    let originalParams = null;

    if (action.type === FETCH_BOOK_NOW_DETAILS) {
      // Clean up any existing timeouts from previous requests
      const existingTimeoutId = sessionStorage.getItem("bookNowTimeoutId");
      if (existingTimeoutId) {
        clearTimeout(parseInt(existingTimeoutId));
        sessionStorage.removeItem("bookNowTimeoutId");
      }

      // Store original request parameters in closure
      originalParams = {
        date: action.payload.date,
        sport: action.payload.sport,
        location: action.payload.location,
      };

      next(apiCallStart({ api: "fetchBookNowDetail" }));

      // // Add timeout to prevent loading state from getting stuck
      // const timeoutId = setTimeout(() => {
      //   next(apiCallComplete({ api: "fetchBookNowDetail" }));
      // }, 30000); // 30 second timeout for sequential API calls

      // Store timeout ID for cleanup
      // sessionStorage.setItem("bookNowTimeoutId", timeoutId);

      // Step 1: Check if booking is allowed on that date
      next(
        apiAction({
          url: getCheckBookingAllowedUrl(
            action.payload.location,
            action.payload.sport,
            action.payload.date.toString()
          ),
          feature: `${FETCH_BOOK_NOW_DETAILS}_HOLIDAY_CHECK`,
          method: "GET",
          meta: { originalParams }, // Pass through meta
        })
      );
    }

    // Step 1: Holiday booking check success
    if (
      action.type === `${FETCH_BOOK_NOW_DETAILS}_HOLIDAY_CHECK ${API_SUCCESS}`
    ) {
      const holidayData = action.payload.response || action.payload;

      // Store in Redux instead of sessionStorage
      next(setHolidayBookingStatus(holidayData));

      // Check if booking is allowed based on holiday status
      if (holidayData.booking_allowed === false) {
        // Create a data structure with bookingAllowed: false
        const params = action.meta?.originalParams || originalParams;
        const blockedData = {
          date: params.date,
          bookingAllowed: false,
          slotsAvailability: {
            turfA: [],
            turfB: [],
            turfC: [],
            turfD: [],
            turfE: [],
          },
          slots: {
            turfA: [],
            turfB: [],
            turfC: [],
            turfD: [],
            turfE: [],
          },
        };

        // Dispatch the blocked data
        next(setBookNowDetail(blockedData));

        // Complete the API call
        next(apiCallComplete({ api: "fetchBookNowDetail" }));

        // Clear the timeout since we got a response
        const timeoutId = sessionStorage.getItem("bookNowTimeoutId");
        if (timeoutId) {
          clearTimeout(parseInt(timeoutId));
          sessionStorage.removeItem("bookNowTimeoutId");
        }

        // Return early without proceeding to other API calls
        return;
      }

      // Step 2: Check if date should follow 'HOLIDAY' or 'NORMAL' routine
      const params = action.meta?.originalParams || originalParams;

      next(
        apiAction({
          url: getCheckDatePresenceUrl(
            params.location,
            params.sport,
            params.date
          ),
          feature: `${FETCH_BOOK_NOW_DETAILS}_DATE_PRESENCE`,
          method: "GET",
          meta: { originalParams: params }, // Pass through meta
        })
      );
    }

    // Step 2: Date presence check success
    if (
      action.type === `${FETCH_BOOK_NOW_DETAILS}_DATE_PRESENCE ${API_SUCCESS}`
    ) {
      const datePresenceData = action.payload.response || action.payload;

      // Store in Redux instead of sessionStorage
      next(setDatePresenceStatus(datePresenceData));

      // Step 3: Get day-mappings to determine schedule
      const params = action.meta?.originalParams || originalParams;

      next(
        apiAction({
          url: getDayMappingsUrl(params.location),
          feature: `${FETCH_BOOK_NOW_DETAILS}_DAY_MAPPINGS`,
          method: "GET",
          meta: { originalParams: params }, // Pass through meta
        })
      );
    }

    // Step 3: Day mappings success
    if (
      action.type === `${FETCH_BOOK_NOW_DETAILS}_DAY_MAPPINGS ${API_SUCCESS}`
    ) {
      const dayMappingsData = action.payload.response || action.payload;

      // Store in Redux instead of sessionStorage
      next(setDayMappings(dayMappingsData.day_mappings));

      // Step 4: Fetch pricing configs with correct schedule
      const params = action.meta?.originalParams || originalParams;

      next(
        apiAction({
          url: getPricingConfigsUrl(params.sport, params.location, params.date),
          feature: `${FETCH_BOOK_NOW_DETAILS}_PRICING`,
          method: "GET",
          meta: { originalParams: params }, // Pass through meta
        })
      );
    }

    // Step 4: Pricing configs success
    if (action.type === `${FETCH_BOOK_NOW_DETAILS}_PRICING ${API_SUCCESS}`) {
      const pricingData = action.payload.response || action.payload;

      // Store in Redux instead of sessionStorage
      next(setPricingConfigs(pricingData));

      // Step 5: Fetch book now details (slots) with correct schedule
      const params = action.meta?.originalParams || originalParams;

      next(
        apiAction({
          url: getAvailableSlotsUrl(params.date, params.sport, params.location),
          feature: `${FETCH_BOOK_NOW_DETAILS}_SLOTS`,
          method: "GET",
          meta: { originalParams: params }, // Pass through meta
        })
      );
    }

    // Step 5: Slots success - Final step to process all data
    if (action.type === `${FETCH_BOOK_NOW_DETAILS}_SLOTS ${API_SUCCESS}`) {
      // Store slots data in Redux instead of sessionStorage
      const slotsData = action.payload.response || action.payload;
      next(setAvailableSlots(slotsData));

      // Clear the timeout since we got a successful response
      const timeoutId = sessionStorage.getItem("bookNowTimeoutId");
      if (timeoutId) {
        clearTimeout(parseInt(timeoutId));
        sessionStorage.removeItem("bookNowTimeoutId");
      }

      // Get all data from Redux state instead of sessionStorage
      const params = action.meta?.originalParams || originalParams;

      if (slotsData) {
        // For now, let the component handle the data processing
        // The middleware has already stored all the necessary data in Redux
        // The component can access it via selectors
        next(apiCallComplete({ api: "fetchBookNowDetail" }));

        // Step 6: Fetch equipment options for Pickle Ball sport
        if (params.sport === "pickle-ball") {
          next(
            apiAction({
              url: `${getEquipmentOptionsUrl(
                "Pickle Ball",
                params.location
              )}?t=${Date.now()}`,
              feature: `${FETCH_BOOK_NOW_DETAILS}_EQUIPMENT`,
              method: "GET",
              meta: { originalParams: params },
            })
          );
        }
      } else {
        next(apiCallComplete({ api: "fetchBookNowDetail" }));
      }
    }

    if (action.type === `${FETCH_BOOK_NOW_DETAILS} ${API_ERROR}`) {
      next(apiCallComplete({ api: "fetchBookNowDetail" }));
    }

    // Error handlers for sequential API calls
    if (
      action.type === `${FETCH_BOOK_NOW_DETAILS}_HOLIDAY_CHECK ${API_ERROR}`
    ) {
      console.error("Holiday booking check failed:", action.payload);
      next(apiCallComplete({ api: "fetchBookNowDetail" }));
    }

    if (
      action.type === `${FETCH_BOOK_NOW_DETAILS}_DATE_PRESENCE ${API_ERROR}`
    ) {
      console.error("Date presence check failed:", action.payload);
      next(apiCallComplete({ api: "fetchBookNowDetail" }));
    }

    if (action.type === `${FETCH_BOOK_NOW_DETAILS}_DAY_MAPPINGS ${API_ERROR}`) {
      console.error("Day mappings fetch failed:", action.payload);
      next(apiCallComplete({ api: "fetchBookNowDetail" }));
    }

    if (action.type === `${FETCH_BOOK_NOW_DETAILS}_PRICING ${API_ERROR}`) {
      console.error("Pricing configs fetch failed:", action.payload);
      next(apiCallComplete({ api: "fetchBookNowDetail" }));
    }

    if (action.type === `${FETCH_BOOK_NOW_DETAILS}_SLOTS ${API_ERROR}`) {
      console.error("Slots fetch failed:", action.payload);
      // Clear the timeout since we got a response (even if it's an error)
      const timeoutId = sessionStorage.getItem("bookNowTimeoutId");
      if (timeoutId) {
        clearTimeout(parseInt(timeoutId));
        sessionStorage.removeItem("bookNowTimeoutId");
      }
      next(apiCallComplete({ api: "fetchBookNowDetail" }));
    }

    if (action.type === VERIFY_PAYMENT) {
      const {
        razorpay_order_id,
        razorpay_payment_id,
        razorpay_signature,
        sport,
        slotDate,
        location,
      } = action.payload;
      next(
        apiAction({
          url: getVerifyPaymentUrl(),
          method: "POST",
          feature: VERIFY_PAYMENT,
          data: {
            razorpay_order_id,
            razorpay_payment_id,
            razorpay_signature,
          },
          meta: { slotDate, sport, location },
        })
      );
    }

    // Step 6: Equipment options success for Pickle Ball
    if (action.type === `${FETCH_BOOK_NOW_DETAILS}_EQUIPMENT ${API_SUCCESS}`) {
      const equipmentData = action.payload.response || action.payload;

      // Store equipment data in Redux instead of sessionStorage
      next(setEquipmentConfigs(equipmentData.available_equipment || []));

      // Complete the equipment API call
      next(apiCallComplete({ api: "fetchEquipmentOptions" }));
    }

    if (action.type === `${VERIFY_PAYMENT} ${API_SUCCESS}`) {
      next(apiCallComplete({ api: "newBooking" }));
      dispatch(
        fetchBookNowDetail({
          date: action.meta.slotDate,
          sport: action.meta.sport,
          location: action.meta.location,
        })
      );
      next(completeBooking());
      next(
        openSnackbar({
          message: "Your payment has been successfully recieved.",
        })
      );
    }

    if (action.type === `${VERIFY_PAYMENT} ${API_ERROR}`) {
      next(apiCallComplete({ api: "newBooking" }));
    }

    if (action.type === NEW_BOOKING) {
      const {
        sport,
        slotDate,
        slotTime,
        duration,
        turfNumber,
        paidAmount,
        totalAmount,
        location,
        equipment_selections,
      } = action.payload;
      next(apiCallStart({ api: "newBooking" }));

      const apiData = {
        sport,
        location,
        turf_number: turfNumber,
        slot_date: slotDate,
        slot_time: slotTime,
        duration,
        amount: paidAmount,
        total_amount: totalAmount,
      };

      // Add equipment_selections if present
      if (equipment_selections && equipment_selections.length > 0) {
        apiData.equipment_selections = equipment_selections;
      }

      next(
        apiAction({
          url: getCreateBookingUrl(),
          feature: NEW_BOOKING,
          method: "POST",
          data: apiData,
        })
      );
    }

    if (action.type === `${NEW_BOOKING} ${API_SUCCESS}`) {
      // The API middleware dispatches apiSuccess with the response data directly
      // action.payload IS the response data, not wrapped in a response property
      const parsedData = parseNewBookingApiData(action.payload);
      next(setNewBookingData(parsedData));
    }

    if (action.type === `${NEW_BOOKING} ${API_ERROR}`) {
      next(apiCallComplete({ api: "newBooking" }));
    }

    if (action.type === MAKE_SLOT_AVAILABLE) {
      const { slotDate, slotTime, turfNumber, sport, location, duration } =
        action.payload;

      next(
        apiAction({
          url: getAvailableSlotsUrl(),
          feature: MAKE_SLOT_AVAILABLE,
          method: "PATCH",
          data: {
            slot_date: slotDate,
            slot_time: slotTime,
            turf_number: turfNumber,
            sport,
            location,
            duration: duration || 1, // Use provided duration or default to 1
          },
        })
      );
    }
  };

export default bookNow;
